import * as ko from "pli/knockout";
import * as pliObservable from "pli/forms-knockoutcontrols";
import { createErrorsCountObservable } from "pli/forms-viewmodel/shared";
import { TextBoxObservable } from "pli/forms-knockoutcontrols/pliTextBoxObservable";
import { EditGrid } from "pli/ko-editgrid/editgrid";

/* tslint:disable */
const DataCache = require("../datacache");
const EntityRegistry = require("../entity-registry");
const uniqueCommentBehaviour = require("../ko-behaviours-uniquetractcomment");
require("pli/forms-knockoutcontrols-picklist");
/* tslint:enable */

export interface TractItemRecord {
    description?: string;
    guid?: string;
    hasNotes?: boolean;
    notes?: string;
    originalIndex?: number;
    rowId?: string;
    tractNumber?: number;
    type?: string;
    isNewRecord: boolean;
}

export interface LandItemRecord extends TractItemRecord {
    format?: string;
    lease?: string;
    lessor?: string;
    location?: string;
    productionType?: string;
    sections?: string;
}

export interface RightItemRecord extends TractItemRecord {
    excluding?: boolean;
    from?: string;
    fromZone?: string;
    product?: string;
    to?: string;
    toZone?: string;
}

export interface CommentItemRecord extends TractItemRecord {
    comment?: string;
}

/**
 * Tract item (base interface).
 */
export interface TractItem {
    // common properties
    guid: ko.Observable<string>;
    notes: TextBoxObservable<string>;
    rowId: ko.Observable<string>;
    tractNumber: TextBoxObservable<number>;
    type: ko.Observable<string>;

    description: ko.PureComputed<string>;

    extras: any;
}


/**
 * Land item class.
 */
export class LandItem implements TractItem {
    constructor(record: LandItemRecord = { isNewRecord: false }) {
        this.guid(record.guid || "");
        this.notes.load(record.notes || "");
        this.rowId(record.rowId);
        this.tractNumber.load(Number(record.tractNumber));
        this.type(record.type || "");

        this.format.load(record.format || "DLS");
        this.lease.load(record.lease || "");
        this.lessor.load(record.lessor || "");
        this.location.load(record.location || "");
        this.notes.load(record.notes || "");
        this.productionType.load(record.productionType || "");
        this.sections.load(record.sections || "");

        this.extras = {
            errorsCount: createErrorsCountObservable(this),
            locationDescription: ko.computed(this._computeLocationDescription, this),
            sectionsDescription: ko.computed(this._computeSectionsDescription, this),
            sectionsLabel: ko.computed(this._computeSectionsLabel, this)
        };
    }

    // common properties
    guid: ko.Observable<string> = ko.observable<string>();
    rowId: ko.Observable<string> = ko.observable<string>();
    tractNumber = pliObservable<number>(null, null, "required", "numeric");
    type: ko.Observable<string> = ko.observable("");
    notes = pliObservable<string>();
    description: ko.PureComputed<string>;

    // land specific properties
    format = pliObservable<string>("DLS", <any>{ behaviours: "picklist", picklistData: DataCache.getObservable("landSystemType") }, "required");
    lease = pliObservable<string>();
    lessor = pliObservable<string>();
    location = pliObservable<string>("", null, "required");
    productionType = pliObservable<string>();
    sections = pliObservable<string>();

    // extra properties
    extras: {
        errorsCount: ko.Computed<number>;
        locationDescription: ko.Computed<string>;
        sectionsDescription: ko.Computed<string>;
        sectionsLabel: ko.Computed<string>;
    };


    /**
     * Computes the hint for the location field based on
     * the format
     */
    private _computeLocationDescription(): string {
        switch (this.format()) {
        case "Free":
            return "Enter any text";
        case "NTS":
            return "Example 94 A 12 H";
        case "PLSS":
            return "Example 5N 1W";
        default:
            return "Example 25 10 4";
        }
    };


    /**
     * Computes the hint for the sections field based on
     * the format
     */
    private _computeSectionsDescription(): string {
        switch (this.format()) {
        case "Free":
            return "Enter any text";
        case "NTS":
            return "Example 1,2,20-35";
        case "PLSS":
            return "Example 1,2,3-4,SW20,N/SW20";
        default:
            return "Example 1,4-7,N8,SW8,9(LSD 3,7-10)";
        }
    };


    /**
     * Computes the label for the sections field based on
     * NTS -> Units, otherwise Sections
     */
    private _computeSectionsLabel(): string {
        switch (this.format()) {
        case "NTS":
            return "Units";
        default:
            return "Sections";
        }
    };
}


/**
 * Right item class.
 */
export class RightItem implements TractItem {
    constructor(record: RightItemRecord = { isNewRecord: false }) {
        this.guid(record.guid || "");
        this.notes.load(record.notes || "");
        this.rowId(record.rowId);
        this.tractNumber.load(Number(record.tractNumber));
        this.type(record.type || "");

        this.excluding.load(record.excluding);
        this.from.load(record.from || null);
        this.fromZone.load(record.fromZone || null);
        this.notes.load(record.notes || "");
        this.product.load(record.product || null);
        this.to.load(record.to || null);
        this.toZone.load(record.toZone || null);

        this.from.subscribe(f => f && f.toLowerCase() === "surface" ? this.fromZone.reset() : null);
        this.to.subscribe(f => f && f.toLowerCase() === "basement" ? this.toZone.reset() : null);

        this.extras = {
            errorsCount: createErrorsCountObservable(this)
        };
    }

    // common properties
    guid: ko.Observable<string> = ko.observable<string>();
    rowId: ko.Observable<string> = ko.observable<string>();
    tractNumber = pliObservable<number>(null, null, "required", "numeric");
    type: ko.Observable<string> = ko.observable("");
    notes = pliObservable<string>();
    description: ko.PureComputed<string>;

    // right specific properties
    excluding = pliObservable<boolean>(false);
    from = pliObservable<string>(null, EntityRegistry.fromZoneQualifier, "picklist");
    fromZone = pliObservable<string>(null, EntityRegistry.zone.getPicklistBehaviourData(), "picklist");
    product = pliObservable<string>(null, EntityRegistry.rightsProduct.getPicklistBehaviourData(), "required", "picklist");
    to = pliObservable<string>(null, EntityRegistry.toZoneQualifier, "picklist");
    toZone = pliObservable<string>(null, EntityRegistry.zone.getPicklistBehaviourData(), "picklist");

    // extra properties
    extras: {
        errorsCount: ko.Computed<number>;
    };
}


/**
 * Comment item class.
 */
export class CommentItem implements TractItem {
    constructor(record: CommentItemRecord = { isNewRecord: false }, editGridReference: EditGrid<TractItem, TractItemRecord>) {
        this.guid(record.guid || "");
        this.notes.load(record.notes || "");
        this.rowId(record.rowId);
        this.tractNumber = pliObservable<number>(Number(record.tractNumber), <any>{ editGridRef: editGridReference }, "required", uniqueCommentBehaviour, "numeric");
        this.type(record.type || "");

        this.comment.load(record.comment || "");

        this.extras = {
            errorsCount: createErrorsCountObservable(this)
        };
    }

    // common properties
    guid: ko.Observable<string> = ko.observable<string>();
    rowId: ko.Observable<string> = ko.observable<string>();
    tractNumber: TextBoxObservable<number>;
    type: ko.Observable<string> = ko.observable("");
    notes = pliObservable<string>();
    description: ko.PureComputed<string>;

    // comment specific property
    comment = pliObservable<string>("", null, "required");

    // extra properties
    extras: {
        errorsCount: ko.Computed<number>
    };
}



// WEBPACK FOOTER //
// ./app/document-landright-ts/landsRightsViewModel.ts